import Header from '@components/Header';
import BrandedBannerHeader from '@containers/branded-banner-header';
import BrandedContactLinkFooter from '@containers/branded-contact-link-footer';
import FooterLinksContainer from '@containers/footer-links';
import {
    BrandingFooter,
    BrandingHeader
} from '@models/branding';
import { createConfigurationValuesService } from '@services/factories';
import * as React from 'react';

interface ContentHeaderProps {
    additionalContent?: React.ReactNode;
}

export interface IBrandingContext {
    header: React.ComponentType<ContentHeaderProps>;
    footer: React.ComponentType;
}

const brandingContext = React.createContext<IBrandingContext | null>(null);

export interface BrandingProviderProps {
    children: React.ReactNode | React.ReactNode[];
}

const BrandingAwareFooter = () => {
    const configurationValuesService = React.useRef(createConfigurationValuesService()).current;
    switch (configurationValuesService.brandingFooter) {
        case BrandingFooter.ContactLink:
            return <BrandedContactLinkFooter/>;
        case BrandingFooter.FooterLinks:
            return <FooterLinksContainer/>;
        default:
            return null;
    }
};

const BrandingAwareHeader = (props: ContentHeaderProps) => {
    const configurationValuesService = React.useRef(createConfigurationValuesService()).current;
    switch (configurationValuesService.brandingHeader) {
        case BrandingHeader.Banner:
            return <BrandedBannerHeader>{props.additionalContent}</BrandedBannerHeader>;
        case BrandingHeader.BannerOffset:
            return <BrandedBannerHeader offset={true}>{props.additionalContent}</BrandedBannerHeader>;
        case BrandingHeader.TopBar:
            return <Header>{props.additionalContent}</Header>;
        default:
            return null;
    }
};

/**
 * Provides the branding context for the branded elements in the application.
 * Currently provides the header and footer.
 * @param children
 */
export const BrandingProvider = ({ children }: BrandingProviderProps) => {
    const context = React.useRef({
        footer: BrandingAwareFooter,
        header: BrandingAwareHeader
    }).current;

    return (
        <brandingContext.Provider value={context}>
            {children}
        </brandingContext.Provider>
    );
};

export const BrandingConsumer = brandingContext.Consumer;

export const useBranding = () => {
    const branding = React.useContext(brandingContext);
    if (!branding) {
        throw new Error('useBranding must be under a BrandingProvider');
    }

    return branding;
};

/**
 * Receiver of the brand aware footer
 */
export const BrandedFooter = () => {
    const { footer: Footer } = useBranding();
    return <Footer/>;
};

/**
 * Receiver of the brand aware header
 */
export const BrandedHeader = (props: ContentHeaderProps) => {
    const { header: HeaderWithBranding } = useBranding();
    return <HeaderWithBranding {...props} />;
};



// WEBPACK FOOTER //
// ./src/components/branding/index.tsx